import type { ReactNode } from 'react';
import { CopyButton } from './CopyButton.js';
import './CodeBlock.css';

export interface CodeBlockProps {
  /** The literal text. Rendered verbatim — no highlighting, no reformatting. */
  children: string;
  /** Shown above the block, e.g. `n8n → MCP Client Tool`. */
  caption?: ReactNode;
  /** The language, for the reader rather than for a highlighter. */
  language?: string;
  /** Offer a copy control. On by default: these exist to be pasted somewhere else. */
  copyable?: boolean;
  /** What the copy button says it is copying, for a screen reader. */
  what?: string;
}

/**
 * A block of configuration meant to be copied into another product.
 *
 * ⚠️ **It scrolls inside itself and never wraps.** A URL, a JSON snippet or a header line that wraps
 * is a line somebody pastes with a break in it, and the failure lands in the *other* product where
 * nothing here can explain it. `overflow-x: auto` on the block keeps the page from scrolling
 * sideways too, which is the repo's standing rule for wide content.
 *
 * ⚠️ **No syntax highlighting, deliberately.** Highlighting means either a parser dependency in the
 * bundle or a hand-rolled tokenizer that will mis-colour something and make a correct snippet look
 * wrong. The value of this component is that what is displayed is exactly what is copied.
 */
export function CodeBlock({ children, caption, language, copyable = true, what }: CodeBlockProps) {
  return (
    <figure className="dpr-code">
      {caption || copyable ? (
        <figcaption className="dpr-code__head">
          <span className="dpr-code__caption">
            {caption}
            {language ? <span className="dpr-code__lang">{language}</span> : null}
          </span>
          {copyable ? <CopyButton value={children} what={what} /> : null}
        </figcaption>
      ) : null}
      {/* `tabIndex` so a keyboard user can scroll a wide block — a scrollable region that cannot be
          focused is unreachable without a mouse. */}
      <pre className="dpr-code__body" tabIndex={0}>
        <code>{children}</code>
      </pre>
    </figure>
  );
}
